/* Petrol Portraits: native parent relationships enforce cascading removal.
 * Edition/quantity checks below are theme checks, not checkout validation. */
(() => {
  'use strict';
  if (window.PPPriority) return;
  const expected = new Map([
    ['15811444703566', '59791703769422'], ['15832085233998', '59791712551246'],
    ['15832118296910', '59791717007694'], ['16064476873038', '60832742080846']
  ]);
  const serviceIds = new Set(expected.values());
  const root = window.Shopify?.routes?.root || '/';
  const messages = document.getElementById('pp-priority-messages')?.dataset || {};
  let queue = Promise.resolve();
  let checkingOut = false;
  const attempts = new WeakMap();
  async function request(path, body) {
    const options = { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } };
    if (body !== undefined) {
      options.method = 'POST';
      if (body instanceof FormData) options.body = body;
      else { options.headers['Content-Type'] = 'application/json'; options.body = JSON.stringify(body); }
    }
    const response = await fetch(root + path, options);
    const data = await response.json();
    if (!response.ok || data.errors || data.status >= 400) throw new Error(messages.error || 'Der Warenkorb konnte nicht geprüft werden. Bitte versuchen Sie es erneut.');
    return data;
  }
  function run(task) {
    const next = queue.then(() => navigator.locks ? navigator.locks.request('pp-priority-cart', task) : task());
    queue = next.catch(() => {});
    return next;
  }
  function notify(text) {
    let status = document.getElementById('pp-priority-status');
    if (!status) {
      status = document.createElement('p'); status.id = 'pp-priority-status'; status.setAttribute('role', 'alert');
      status.style.cssText = 'padding:16px;margin:16px;border:1px solid currentColor;';
      (document.getElementById('MainContent') || document.body).prepend(status);
    }
    status.textContent = text;
  }
  function correction(cart) {
    const seen = new Set();
    for (const [index, item] of cart.items.entries()) {
      if (!serviceIds.has(String(item.variant_id))) continue;
      const parentKey = item.parent_relationship?.parent_key;
      const parent = cart.items.find(line => line.key === parentKey);
      const valid = parent && expected.get(String(parent.product_id)) === String(item.variant_id) && !seen.has(parentKey);
      if (!valid) return { line: index + 1, quantity: 0, removed: true };
      seen.add(parentKey);
      if (item.quantity !== parent.quantity) return { line: index + 1, quantity: parent.quantity };
    }
    return null;
  }
  async function reconcile(cart) {
    let changed = false, removed = false;
    const maxPasses = cart.items.length * 2 + 4;
    for (let pass = 0; pass < maxPasses; pass++) {
      const fix = correction(cart);
      if (!fix) return { cart, changed, removed };
      cart = await request('cart/change.js', { line: fix.line, quantity: fix.quantity });
      changed = true; removed ||= Boolean(fix.removed);
    }
    throw new Error(messages.error);
  }
  async function checkedCart() {
    const result = await reconcile(await request('cart.js'));
    if (result.removed) notify(messages.cleaned);
    return result;
  }
  async function change({ id, quantity, sections, sections_url }) {
    return run(async () => {
      let cart = await request('cart.js');
      const index = cart.items.findIndex(item => item.key === id);
      if (index !== -1) cart = await request('cart/change.js', { line: index + 1, quantity });
      const result = await reconcile(cart);
      if (result.removed) notify(messages.cleaned);
      return request('cart/update.js', { updates: {}, sections, sections_url });
    });
  }
  function setFormError(form, text) {
    const addon = form.querySelector('[data-pp-priority-addon]');
    const error = addon?.querySelector('.pp-priority-addon__error');
    if (error) { error.textContent = text; error.classList.toggle('is-visible', Boolean(text)); }
    addon?.classList.toggle('pp-priority-addon--error', Boolean(text));
  }
  async function addPair(form, data, serviceId) {
    let attempt = attempts.get(form);
    if (!attempt) { attempt = { token: crypto.randomUUID() }; attempts.set(form, attempt); }
    let cart = await request('cart.js');
    let parent = cart.items.find(item => item.properties?._pp_pair === attempt.token && item.properties?._pp_role === 'portrait');
    if (!parent) {
      data.set('properties[_pp_pair]', attempt.token); data.set('properties[_pp_role]', 'portrait');
      try { await request('cart/add.js', data); }
      catch (error) {
        cart = await request('cart.js');
        parent = cart.items.find(item => item.properties?._pp_pair === attempt.token && item.properties?._pp_role === 'portrait');
        if (!parent) throw error;
      }
      cart = await request('cart.js');
      parent = cart.items.find(item => item.properties?._pp_pair === attempt.token && item.properties?._pp_role === 'portrait');
    }
    if (!parent || expected.get(String(parent.product_id)) !== serviceId) throw new Error(messages.error);
    let child = cart.items.find(item => item.parent_relationship?.parent_key === parent.key && String(item.variant_id) === serviceId);
    if (!child) {
      try {
        await request('cart/add.js', { items: [{ id: serviceId, quantity: parent.quantity, parent_line_key: parent.key,
          properties: { _pp_pair: attempt.token, _pp_role: 'priority' } }] });
      } catch (error) {
        cart = await request('cart.js');
        child = cart.items.find(item => item.parent_relationship?.parent_key === parent.key && String(item.variant_id) === serviceId);
        if (!child) {
          const parentIndex = cart.items.findIndex(item => item.properties?._pp_pair === attempt.token && item.properties?._pp_role === 'portrait');
          if (parentIndex !== -1) await request('cart/change.js', { line: parentIndex + 1, quantity: 0 });
          attempts.delete(form); throw error;
        }
      }
    }
    const result = await checkedCart();
    parent = result.cart.items.find(item => item.properties?._pp_pair === attempt.token && item.properties?._pp_role === 'portrait');
    child = result.cart.items.find(item => item.parent_relationship?.parent_key === parent?.key && String(item.variant_id) === serviceId);
    if (!parent || !child || parent.quantity !== child.quantity) throw new Error(messages.partial);
    attempts.delete(form);
  }
  // Existing capture handlers validate the upload, permissions and variant first.
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.pp-product-form') || event.defaultPrevented) return;
    const checkbox = form.querySelector('.pp-priority-addon__input');
    if (!checkbox?.checked) {
      if (attempts.has(form)) { event.preventDefault(); setFormError(form, messages.partial); }
      return;
    }
    event.preventDefault();
    if (form.dataset.ppPriorityBusy === 'true') return;
    const productId = form.closest('[data-product-id]')?.dataset.productId;
    const serviceId = expected.get(productId);
    if (!serviceId || checkbox.disabled) return setFormError(form, messages.error);
    const data = new FormData(form); // Preserve File objects and external form-associated fields.
    const button = form.querySelector('.pp-add-to-cart');
    form.dataset.ppPriorityBusy = 'true';
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    setFormError(form, '');
    run(() => addPair(form, data, serviceId)).then(() => window.location.assign(root + 'cart')).catch(error => {
      setFormError(form, error.message || messages.error); form.dataset.ppPriorityBusy = 'false';
      if (button) { button.removeAttribute('aria-busy'); button.disabled = button.dataset.ppVariantAvailable === 'false'; }
    });
  });
  function editedQuantities() {
    return [...document.querySelectorAll('#cart-form [data-key]')].flatMap(row => {
      const input = row.querySelector('input[name="updates[]"]');
      const quantity = Number(input?.value);
      return input && !input.disabled && input.value !== input.defaultValue && Number.isInteger(quantity) && quantity >= 0
        ? [{ id: row.dataset.key, quantity }] : [];
    });
  }
  async function checkout(event) {
    event.preventDefault(); event.stopImmediatePropagation();
    if (checkingOut) return;
    checkingOut = true;
    const edits = editedQuantities();
    const buttons = [...document.querySelectorAll('[name="checkout"]')];
    buttons.forEach(button => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
    try {
      await run(async () => {
        let cart = await request('cart.js');
        for (const edit of edits) {
          const index = cart.items.findIndex(item => item.key === edit.id);
          if (index !== -1) cart = await request('cart/change.js', { line: index + 1, quantity: edit.quantity });
        }
        await reconcile(cart);
        const note = document.querySelector('textarea[name="note"]');
        if (note) await request('cart/update.js', { note: note.value });
      });
      // Do not replay stale positional updates[] after asynchronous cart changes.
      window.location.assign(root + 'checkout');
    } catch (error) {
      notify(error.message || messages.error); checkingOut = false;
      buttons.forEach(button => { button.disabled = false; button.removeAttribute('aria-busy'); });
    }
  }
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const target = event.target.closest('[name="checkout"], a[href]');
    if (!target) return;
    if (target.matches('[name="checkout"]')) return void checkout(event);
    const url = new URL(target.href, location.origin);
    if (url.origin === location.origin && /\/checkout\/?$/.test(url.pathname)) void checkout(event);
  }, true);
  document.addEventListener('submit', event => {
    if (event.target instanceof HTMLFormElement && event.target.id === 'cart-form') void checkout(event);
  }, true);
  window.PPPriority = Object.freeze({ change, check: () => run(checkedCart) });
  function checkPage() {
    run(checkedCart).then(result => {
      if (result.changed && /\/cart\/?$/.test(location.pathname)) {
        if (result.removed) { try { sessionStorage.setItem('pp-priority-cleaned', '1'); } catch {} }
        location.reload();
      }
    }).catch(error => notify(error.message || messages.error));
  }
  try {
    if (sessionStorage.getItem('pp-priority-cleaned')) { notify(messages.cleaned); sessionStorage.removeItem('pp-priority-cleaned'); }
  } catch {}
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    checkingOut = false;
    document.querySelectorAll('[name="checkout"][aria-busy="true"]').forEach(button => {
      button.disabled = false; button.removeAttribute('aria-busy');
    });
    document.querySelectorAll('.pp-product-form[data-pp-priority-busy="true"]').forEach(form => {
      form.dataset.ppPriorityBusy = 'false';
      const button = form.querySelector('.pp-add-to-cart');
      if (button) { button.removeAttribute('aria-busy'); button.disabled = button.dataset.ppVariantAvailable === 'false'; }
    });
    checkPage();
  });
  checkPage();
})();
